@import '../../themes/graphite.globals';

// Checkbox
// --------------------------------------------------

:host {
  /**
   * @prop --label-color: Color of the label
   *
   * @prop --control-size: Size of the control
   *
   * @prop --control-border-color: Border color of the control
   * @prop --control-background-color: Background color of the control
   *
   * @prop --control-border-color-hover: Border color of the control on hover
   * @prop --control-background-color-hover: Background color of the control on hover
   *
   * @prop --control-color-checked: Color of the control when checked (the checkmark icon)
   * @prop --control-border-color-checked: Border color of the control when checked
   * @prop --control-background-color-checked: Background color of the control when checked
   *
   * @prop --control-border-color-checked-hover: Border color of the control when checked on hover
   * @prop --control-background-color-checked-hover: Background color of the control when checked on hover
   *
   * @prop --control-border-color-checked-focus: Border color of the control when checked on focus
   * @prop --control-background-color-checked-focus: Background color of the control when checked on focus
   *
   * @prop --focus-ring: The focus ring style to use when the checkbox receives focus, a `box-shadow` property.
   */

  --label-color: var(--gr-color-dark-tint);
  --label-margin-top-adjustment: -7px;
  --label-margin-bottom-adjustment: -4px;

  --control-size: var(--gr-toggle-size);

  --control-border-color: var(--gr-color-light-shade);
  --control-background-color: var(--gr-color-white);

  --control-border-color-hover: var(--gr-color-medium);
  --control-background-color-hover: var(--gr-color-white);

  --control-color-checked: var(--gr-color-primary-contrast);
  --control-border-color-checked: var(--gr-color-primary);
  --control-background-color-checked: var(--gr-color-primary);

  --control-border-color-checked-hover: var(--gr-color-primary-shade);
  --control-background-color-checked-hover: var(--gr-color-primary-shade);

  --control-border-color-checked-focus: var(--gr-color-primary-shade);
  --control-background-color-checked-focus: var(--gr-color-primary-shade);

  --focus-ring: 0 0 0 var(--gr-focus-ring-width) rgb(var(--gr-color-primary-rgb), 0.33);

  display: inline-block;
  box-sizing: border-box;

  & *,
  & *:before,
  & *:after {
    box-sizing: inherit;
  }
}

:host(.checkbox-disabled) {
  pointer-events: none;
}

// Alignment modifiers
// --------------------------------------------------
.checkbox-top {
  align-items: flex-start;
}

.checkbox-center {
  align-items: center;
}

.checkbox-bottom {
  align-items: flex-end;
}

.checkbox {
  display: inline-flex;
  font-family: var(--gr-font-family);
  font-size: var(--gr-form-element-font-size-medium);
  font-weight: var(--gr-font-weight-normal);
  color: var(--label-color);
  vertical-align: middle;
  cursor: pointer;
}

.checkbox-icon {
  display: inline-flex;
  width: var(--control-size);
  height: var(--control-size);

  svg {
    width: 100%;
    height: 100%;
  }
}

.checkbox-control {
  flex: 0 0 auto;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--control-size);
  height: var(--control-size);
  border: solid 1px var(--control-border-color);
  border-radius: var(--gr-form-element-border-radius-small);
  background-color: var(--control-background-color);
  color: transparent;
  transition: 150ms border-color, 150ms background-color, 150ms color, 150ms box-shadow;

  input[type='checkbox'] {
    position: absolute;
    opacity: 0;
    padding: 0;
    margin: 0;
    pointer-events: none;
  }
}

.checkbox-control-circle {
  border-radius: var(--border-radius-circle);
}

// Hover
.checkbox:not(.checkbox-checked):not(.checkbox-disabled) .checkbox-control:hover {
  border-color: var(--control-border-color-hover);
  background-color: var(--control-background-color-hover);
}

// Focus
.checkbox.checkbox-focused:not(.checkbox-checked):not(.checkbox-disabled) .checkbox-control {
  border-color: var(--control-border-color-hover);
  background-color: var(--control-background-color-hover);
  box-shadow: var(--focus-ring);
}

// Checked/indeterminate
.checkbox-checked .checkbox-control,
.checkbox-indeterminate .checkbox-control {
  color: var(--control-color-checked);
  border-color: var(--control-border-color-checked);
  background-color: var(--control-background-color-checked);
}

// Checked/indeterminate + hover
.checkbox.checkbox-checked:not(.checkbox-disabled) .checkbox-control:hover,
.checkbox.checkbox-indeterminate:not(.checkbox-disabled) .checkbox-control:hover {
  border-color: var(--control-border-color-checked-hover);
  background-color: var(--control-background-color-checked-hover);
}

// Checked/indeterminate + focus
.checkbox.checkbox-checked:not(.checkbox-disabled).checkbox-focused .checkbox-control,
.checkbox.checkbox-indeterminate:not(.checkbox-disabled).checkbox-focused .checkbox-control {
  border-color: var(--control-border-color-checked-focus);
  background-color: var(--control-background-color-checked-focus);
  box-shadow: var(--focus-ring);
}

// Invalid
.checkbox:not(.checkbox-disabled).checkbox-invalid .checkbox-control {
  border-color: var(--gr-form-element-invalid-text-color);
}

// Checked/indeterminate + invalid
.checkbox.checkbox-checked:not(.checkbox-disabled).checkbox-invalid .checkbox-control,
.checkbox.checkbox-indeterminate:not(.checkbox-disabled).checkbox-invalid .checkbox-control {
  border-color: var(--gr-form-element-invalid-text-color);
  background-color: var(--gr-form-element-invalid-text-color);
}

// Disabled
.checkbox-disabled {
  opacity: 0.5;
}

.checkbox-label {
  line-height: var(--gr-line-height-normal);
  margin-left: 0.5em;
  user-select: none;
}

.checkbox-top .checkbox-label {
  margin-top: var(--label-margin-top-adjustment);
}

.checkbox-bottom .checkbox-label {
  margin-bottom: var(--label-margin-bottom-adjustment);
}
// Invalid text
.invalid-text {
  display: none;
}

// Invalid text
.checkbox-invalid-text {
  display: none;
}

:host(.checkbox-has-invalid-text) {
  display: block;

  .checkbox-invalid-text {
    display: flex;
    margin-left: -2px;
    line-height: var(--gr-line-height-normal);
    color: var(--gr-form-element-invalid-text-color);
    margin-top: var(--gr-spacing-xxx-small);
    font-size: var(--gr-form-element-invalid-text-font-size-medium);
    min-height: 1.875rem;

    .icon {
      margin-top: var(--gr-spacing-xxx-small);
      margin-right: var(--gr-spacing-xx-small);

      svg {
        width: 1.4em;
        height: 1.4em;
      }
    }
  }
}
